import {
  AutoColumn,
  Button,
  Text,
  Box,
  DynamicSection,
  Message,
  Flex,
  MessageText,
  ScanLink,
  LinkExternal,
} from '@pancakeswap/uikit'
import styled from 'styled-components'
import { ChainLinkSupportChains } from 'state/info/constant'
import { useIsExpertMode } from '@pancakeswap/utils/user'
import { useTranslation } from '@pancakeswap/localization'
import { ReactNode, useCallback, useMemo } from 'react'
import { Percent, Pair, Currency } from '@pancakeswap/sdk'
import { NextRouter } from 'next/router'

import { CommitButton } from 'components/CommitButton'
import EBox from 'components/EBox'
import CurrencyInputPanel from 'components/ECurrencyInputPanel'
import { Field } from 'state/mint/actions'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { Bound } from 'config/constants/types'
import { LP2ChildrenProps } from 'views/AddLiquidity'
import { logGTMClickAddLiquidityEvent } from 'utils/customGTMEventTracking'
import { CommonBasesType } from 'components/SearchModal/types'
import { getBlockExploreLink } from 'utils'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { PairState } from 'hooks/usePairs'

import ApproveLiquidityTokens from 'views/AddLiquidityV3/components/ApproveLiquidityTokens'
import { InfoBox } from '@pancakeswap/widgets-internal'
import { HideMedium, MediumOnly, RightContainer } from './V3FormView'
import RangeSelector from './V3FormView/components/RangeSelector'
import { SELECTOR_TYPE } from '../types'

const Wrapper = styled(Flex)`
  position: relative;
  flex-direction: column;
  justify-content: space-between;
  display: flex;
  gap: 0.5rem;
`

export default function V2FormView({
  formattedAmounts,
  addIsUnsupported,
  addIsWarning,
  shouldShowApprovalGroup,
  approveACallback,
  revokeACallback,
  currentAllowanceA,
  approvalA,
  approvalB,
  approveBCallback,
  revokeBCallback,
  currentAllowanceB,
  showFieldBApproval,
  showFieldAApproval,
  currencies,
  buttonDisabled,
  onAdd,
  onPresentAddLiquidityModal,
  errorText,
  onFieldAInput,
  onFieldBInput,
  maxAmounts,
  isOneWeiAttack,
  pair,
  pairState,
  onCurrencyASelect,
  onCurrencyBSelect,
  handleCurrencySelect,
  router,
  disableCurrencySelect = false,
  onDismiss
  // selectorType,
}: LP2ChildrenProps & {
  handleCurrencySelect: (currencyNew: Currency, currencyIdOther?: string) => (string | undefined)[]
  router?: NextRouter
  disableCurrencySelect?: boolean
  // selectorType: SELECTOR_TYPE
}) {
  const mockFn = useCallback(() => undefined, [])

  const { chainId } = useActiveChainId()
  const { account, isWrongNetwork } = useActiveWeb3React()
  const { t } = useTranslation()
  const expertMode = useIsExpertMode()
  const pairExplorerLink = useMemo(
    () => pair && getBlockExploreLink(Pair.getAddress(pair.token0, pair.token1), 'address', chainId),
    [pair, chainId],
  )

  let buttons: ReactNode = null
  if (addIsUnsupported || addIsWarning) {
    buttons = (
      <Button disabled mb="4px">
        {t('Unsupported Asset')}
      </Button>
    )
  } else if (!account) {
    buttons = <ConnectWalletButton width="100%" />
  } else if (isWrongNetwork) {
    buttons = <CommitButton />
  } else {
    buttons = (
      <AutoColumn gap="md">
        <ApproveLiquidityTokens
          approvalA={approvalA}
          approvalB={approvalB}
          showFieldAApproval={showFieldAApproval}
          showFieldBApproval={showFieldBApproval}
          approveACallback={approveACallback}
          approveBCallback={approveBCallback}
          revokeACallback={revokeACallback}
          revokeBCallback={revokeBCallback}
          currencies={currencies}
          currentAllowanceA={currentAllowanceA}
          currentAllowanceB={currentAllowanceB}
          shouldShowApprovalGroup={shouldShowApprovalGroup}
        />
        {isOneWeiAttack ? (
          <Message variant="warning">
            <Flex flexDirection="column">
              <MessageText>
                {t(
                  'Adding liquidity to this V2 pair is currently not available on 9Inch UI. Please follow the instructions to resolve it using blockchain explorer.',
                )}
              </MessageText>
              {/* <LinkExternal
                href="https://docs.pre-prod.v3-cbr.pages.dev/products/pancakeswap-exchange/faq#why-cant-i-add-liquidity-to-a-pair-i-just-created"
                mt="0.25rem"
              >
                {t('Learn more how to fix')}
              </LinkExternal> */}
              <ScanLink
                useBscCoinFallback={ChainLinkSupportChains.includes(chainId)}
                href={pairExplorerLink}
                mt="0.25rem"
              >
                {t('View pool on explorer')}
              </ScanLink>
            </Flex>
          </Message>
        ) : null}
        <CommitButton
          variant={buttonDisabled ? 'danger' : 'primary'}
          onClick={() => {
            if (onDismiss) {
              onDismiss()
            }
            // eslint-disable-next-line no-unused-expressions
            expertMode ? onAdd() : onPresentAddLiquidityModal()
            logGTMClickAddLiquidityEvent()
          }}
          disabled={buttonDisabled}
        >
          {errorText || t('Add')}
        </CommitButton>
      </AutoColumn>
    )
  }

  const handleCurrencyASelect = useCallback(
    (currencyANew: Currency) => {
      const [idA, idB] = handleCurrencySelect(currencyANew, currencies[Field.CURRENCY_B]?.wrapped.address)
      router?.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            currency: [idA, idB],
            v2: 'true', // Always keep v2 flag
          },
        },
        undefined,
        { shallow: true },
      )
    },
    [handleCurrencySelect, currencies, router],
  )

  const handleCurrencyBSelect = useCallback(
    (currencyBNew: Currency) => {
      const [idB, idA] = handleCurrencySelect(currencyBNew, currencies[Field.CURRENCY_A]?.wrapped.address)
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            currency: [idA, idB],
            v2: 'true', // Always keep v2 flag
          },
        },
        undefined,
        { shallow: true },
      )
    },
    [handleCurrencySelect, currencies, router],
  )

  return (
    <Wrapper>
      {
        pairState===PairState.NOT_EXISTS &&
        <Message variant="warning">
          <Box>
            <Text color="textSubtle">
              {t('You are the first liquidity provider.')}
            </Text>
            <Text color="textSubtle">
              {t('The ratio of tokens you add will set the price of this pair.')}
            </Text>
            <Text color="textSubtle">
              {t('Once you are happy with the rate click supply to review.')}
            </Text>
          </Box>
        </Message>
      }
      <CurrencyInputPanel
        maxAmount={maxAmounts[Field.CURRENCY_A]}
        showUSDPrice
        onMax={() => {
          onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
        }}
        onPercentInput={(percent) => {
          if (maxAmounts[Field.CURRENCY_A]) {
            onFieldAInput(maxAmounts[Field.CURRENCY_A]?.multiply(new Percent(percent, 100)).toExact() ?? '')
          }
        }}
        value={formattedAmounts[Field.CURRENCY_A] ?? '0'}
        onUserInput={onFieldAInput}
        showQuickInputButtons={[25, 50]}
        showMaxButton
        currency={currencies[Field.CURRENCY_A]}
        id="add-liquidity-input-tokena"
        showCommonBases
        commonBasesType={CommonBasesType.LIQUIDITY}
        onCurrencySelect={handleCurrencyASelect}
        otherCurrency={currencies[Field.CURRENCY_B]}
        disableCurrencySelect={disableCurrencySelect}
      />

      <CurrencyInputPanel
        showUSDPrice
        onPercentInput={(percent) => {
          if (maxAmounts[Field.CURRENCY_B]) {
            onFieldBInput(maxAmounts[Field.CURRENCY_B]?.multiply(new Percent(percent, 100)).toExact() ?? '')
          }
        }}
        onMax={() => {
          onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
        }}
        maxAmount={maxAmounts[Field.CURRENCY_B]}
        value={formattedAmounts[Field.CURRENCY_B] ?? '0'}
        onUserInput={onFieldBInput}
        showQuickInputButtons={[25, 50]}
        showMaxButton
        currency={currencies[Field.CURRENCY_B]}
        id="add-liquidity-input-tokenb"
        showCommonBases
        commonBasesType={CommonBasesType.LIQUIDITY}
        onCurrencySelect={handleCurrencyBSelect}
        otherCurrency={currencies[Field.CURRENCY_A]}
        disableCurrencySelect={disableCurrencySelect}
      />

      {buttons}
    </Wrapper>
  )
}
